import { makeFunctionReference } from "convex/server";
import type { FunctionReference } from "convex/server";

// Shared voice recording + transcription helpers (plan todo 13), extracted
// from Chat.tsx so the chat composer AND the standalone voice logging flow
// (todo 15, VoiceLogFlow) use ONE recorder implementation.
//
// CONTRACT for todo 15 (VoiceLogFlow):
// - startVoiceRecording({ onBlob, onRecorderError }) acquires the mic, picks a
//   supported MIME type, records, and resolves to an ActiveVoiceRecording.
//   onBlob fires exactly once per stop() with the assembled recording (only
//   when non-empty). Use recording.stop() to finish and KEEP the blob, or
//   recording.cancel() to tear down WITHOUT emitting a blob (unmount path).
// - Start failures are typed: "unsupported" (no mediaDevices/MediaRecorder),
//   "no-supported-mime", "mic-permission". Map them through
//   VOICE_RECORDING_ERROR_MESSAGES for user-facing copy.
// - encodeBlobAsBase64(blob) strips the data-URL prefix for transcribeAudio.
// - transcribeAudioRef is the FunctionReference for useAction; args
//   { audioBase64, mimeType } -> { text, model, language }.

export type TranscribeAudioArgs = {
  readonly audioBase64: string;
  readonly mimeType: string;
};

export type TranscribeAudioResult = {
  readonly text: string;
  readonly model: string;
  readonly language: string;
};

export const transcribeAudioRef: FunctionReference<
  "action",
  "public",
  TranscribeAudioArgs,
  TranscribeAudioResult
> = makeFunctionReference("audio:transcribeAudio");

export type VoiceRecordingStartError =
  | "unsupported"
  | "no-supported-mime"
  | "mic-permission";

export const VOICE_RECORDING_ERROR_MESSAGES: Record<VoiceRecordingStartError, string> = {
  unsupported: "Voice recording is not supported in this browser.",
  "no-supported-mime": "This browser cannot encode a supported audio format.",
  "mic-permission": "Microphone permission is required to record audio.",
};

export function isVoiceRecordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== "undefined"
  );
}

export const PREFERRED_AUDIO_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
] as const;

export function pickSupportedAudioMimeType(): string | null {
  return (
    PREFERRED_AUDIO_MIME_TYPES.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null
  );
}

export interface ActiveVoiceRecording {
  readonly mimeType: string;
  // Finishes the recording: onstop fires and onBlob receives the assembled
  // blob (when non-empty). Mic tracks are stopped immediately.
  stop(): void;
  // Tears down WITHOUT emitting a blob (unmount / abort). Handlers are
  // detached before the recorder and tracks are stopped.
  cancel(): void;
}

export interface VoiceRecordingHandlers {
  readonly onBlob: (blob: Blob) => void;
  readonly onRecorderError?: () => void;
}

export type StartVoiceRecordingResult =
  | { readonly ok: true; readonly recording: ActiveVoiceRecording }
  | { readonly ok: false; readonly error: VoiceRecordingStartError };

export async function startVoiceRecording(
  handlers: VoiceRecordingHandlers,
): Promise<StartVoiceRecordingResult> {
  if (!isVoiceRecordingSupported()) {
    console.warn("[VoiceRecorder] Recording unsupported", {
      hasMediaDevices:
        typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia),
      hasMediaRecorder: typeof MediaRecorder !== "undefined",
    });
    return { ok: false, error: "unsupported" };
  }
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const preferredMime = pickSupportedAudioMimeType();
    if (preferredMime === null && !MediaRecorder.isTypeSupported("")) {
      stream.getTracks().forEach((track) => track.stop());
      return { ok: false, error: "no-supported-mime" };
    }
    const recorder = preferredMime !== null
      ? new MediaRecorder(stream, { mimeType: preferredMime })
      : new MediaRecorder(stream);
    const micStream = stream;
    let chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onerror = () => handlers.onRecorderError?.();
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      chunks = [];
      if (blob.size > 0) handlers.onBlob(blob);
    };
    recorder.start();
    console.log("[VoiceRecorder] Recording started", { mimeType: recorder.mimeType });
    return {
      ok: true,
      recording: {
        mimeType: recorder.mimeType,
        stop() {
          if (recorder.state !== "inactive") recorder.stop();
          micStream.getTracks().forEach((track) => track.stop());
        },
        cancel() {
          recorder.ondataavailable = null;
          recorder.onerror = null;
          recorder.onstop = null;
          chunks = [];
          if (recorder.state !== "inactive") recorder.stop();
          micStream.getTracks().forEach((track) => track.stop());
        },
      },
    };
  } catch (error: unknown) {
    stream?.getTracks().forEach((track) => track.stop());
    console.error("[VoiceRecorder] Microphone access failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return { ok: false, error: "mic-permission" };
  }
}

// FileReader data-URL -> raw base64 (the transcribeAudio action expects the
// payload WITHOUT the "data:<mime>;base64," prefix).
export async function encodeBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Audio encoding did not return a data URL"));
        return;
      }
      const separatorIndex = reader.result.indexOf(",");
      resolve(separatorIndex >= 0 ? reader.result.slice(separatorIndex + 1) : reader.result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Audio encoding failed"));
    reader.readAsDataURL(blob);
  });
}
